import { createUseStyles } from 'react-jss';
import { BreadCrumbElementTypes } from '../types/index.js';
import { hideScrollbars } from '../../../../foundation/styleHelpers/hideScrollbars/hideScrollbars/index.js';
import { isRightToLeft, isLeftToRight } from '../../../../foundation/styleHelpers/rtl/rtl/index.js';
import { ControlTextColors } from '../../../../foundation/colors/controlTextColors/controlTextColors/index.js';
import { BreakPointsMedia } from '../../../../foundation/responsive/responsive/index.js';
import { Timings } from '../../../../foundation/timings/timings/index.js';
import { Easing } from '../../../../foundation/easing/easing/index.js';
import { SecondaryColors } from '../../../../foundation/colors/secondaryColors/secondaryColors/index.js';
import { canTouch, canHover } from '../../../../foundation/styleHelpers/hoverTouch/hoverTouch/index.js';

const useBreadCrumbsStyles = createUseStyles({
    root: {
        display: 'block',
        height: ({ sizes }) => sizes?.Default.value?.blockHeight,
        listStyleType: 'none',
        overflowX: 'auto',
        overflowY: 'hidden',
        ...hideScrollbars,
        whiteSpace: 'nowrap',
        [BreakPointsMedia.XXL]: {
            height: ({ sizes }) => sizes?.XXL.value?.blockHeight,
        },
        [BreakPointsMedia.XL]: {
            height: ({ sizes }) => sizes?.XL.value?.blockHeight,
        },
        [BreakPointsMedia.LG]: {
            height: ({ sizes }) => sizes?.LG.value?.blockHeight,
        },
        [BreakPointsMedia.MD]: {
            height: ({ sizes }) => sizes?.MD.value?.blockHeight,
        },
        [BreakPointsMedia.SM]: {
            height: ({ sizes }) => sizes?.SM.value?.blockHeight,
        },
        [BreakPointsMedia.XS]: {
            height: ({ sizes }) => sizes?.XS.value?.blockHeight,
        },
        [BreakPointsMedia.XXS]: {
            height: ({ sizes }) => sizes?.XXS.value?.blockHeight,
        },
    },
    item: {
        display: 'inline-block',
        verticalAlign: 'top',
        height: '100%',
        padding: ({ sizes }) => `0 ${sizes?.Default.value?.paddingHorizontal}`,
        [BreakPointsMedia.XXL]: {
            padding: ({ sizes }) => `0 ${sizes?.XXL.value?.paddingHorizontal}`,
        },
        [BreakPointsMedia.XL]: {
            padding: ({ sizes }) => `0 ${sizes?.XL.value?.paddingHorizontal}`,
        },
        [BreakPointsMedia.LG]: {
            padding: ({ sizes }) => `0 ${sizes?.LG.value?.paddingHorizontal}`,
        },
        [BreakPointsMedia.MD]: {
            padding: ({ sizes }) => `0 ${sizes?.MD.value?.paddingHorizontal}`,
        },
        [BreakPointsMedia.SM]: {
            padding: ({ sizes }) => `0 ${sizes?.SM.value?.paddingHorizontal}`,
        },
        [BreakPointsMedia.XS]: {
            padding: ({ sizes }) => `0 ${sizes?.XS.value?.paddingHorizontal}`,
        },
        [BreakPointsMedia.XXS]: {
            padding: ({ sizes }) => `0 ${sizes?.XXS.value?.paddingHorizontal}`,
        },
    },
    itemHasIcon: {
        position: 'relative',
        paddingInlineEnd: ({ sizes }) => `calc((${sizes?.Default?.value?.paddingHorizontal} * 2) + ${sizes?.Default?.value?.iconWidth}px)`,
        [BreakPointsMedia.XXL]: {
            paddingInlineEnd: ({ sizes }) => `calc((${sizes?.XXL?.value?.paddingHorizontal} * 2) + ${sizes?.XXL?.value?.iconWidth}px)`,
        },
        [BreakPointsMedia.XL]: {
            paddingInlineEnd: ({ sizes }) => `calc((${sizes?.XL?.value?.paddingHorizontal} * 2) + ${sizes?.XL?.value?.iconWidth}px)`,
        },
        [BreakPointsMedia.LG]: {
            paddingInlineEnd: ({ sizes }) => `calc((${sizes?.LG?.value?.paddingHorizontal} * 2) + ${sizes?.LG?.value?.iconWidth}px)`,
        },
        [BreakPointsMedia.MD]: {
            paddingInlineEnd: ({ sizes }) => `calc((${sizes?.MD?.value?.paddingHorizontal} * 2) + ${sizes?.MD?.value?.iconWidth}px)`,
        },
        [BreakPointsMedia.SM]: {
            paddingInlineEnd: ({ sizes }) => `calc((${sizes?.SM?.value?.paddingHorizontal} * 2) + ${sizes?.SM?.value?.iconWidth}px)`,
        },
        [BreakPointsMedia.XS]: {
            paddingInlineEnd: ({ sizes }) => `calc((${sizes?.XS?.value?.paddingHorizontal} * 2) + ${sizes?.XS?.value?.iconWidth}px)`,
        },
        [BreakPointsMedia.XXS]: {
            paddingInlineEnd: ({ sizes }) => `calc((${sizes?.XXS?.value?.paddingHorizontal} * 2) + ${sizes?.XXS?.value?.iconWidth}px)`,
        },
    },
    icon: {
        display: 'inline-block',
        width: ({ sizes }) => sizes?.Default.value?.iconWidth,
        height: ({ sizes }) => sizes?.Default.value?.iconHeight,
        position: 'absolute',
        top: 0,
        bottom: 0,
        margin: 'auto 0',
        color: ControlTextColors.default,
        [BreakPointsMedia.XXL]: {
            width: ({ sizes }) => sizes?.XXL.value?.iconWidth,
            height: ({ sizes }) => sizes?.XXL.value?.iconHeight,
        },
        [BreakPointsMedia.XL]: {
            width: ({ sizes }) => sizes?.XL.value?.iconWidth,
            height: ({ sizes }) => sizes?.XL.value?.iconHeight,
        },
        [BreakPointsMedia.LG]: {
            width: ({ sizes }) => sizes?.LG.value?.iconWidth,
            height: ({ sizes }) => sizes?.LG.value?.iconHeight,
        },
        [BreakPointsMedia.MD]: {
            width: ({ sizes }) => sizes?.MD.value?.iconWidth,
            height: ({ sizes }) => sizes?.MD.value?.iconHeight,
        },
        [BreakPointsMedia.SM]: {
            width: ({ sizes }) => sizes?.SM.value?.iconWidth,
            height: ({ sizes }) => sizes?.SM.value?.iconHeight,
        },
        [BreakPointsMedia.XS]: {
            width: ({ sizes }) => sizes?.XS.value?.iconWidth,
            height: ({ sizes }) => sizes?.XS.value?.iconHeight,
        },
        [BreakPointsMedia.XXS]: {
            width: ({ sizes }) => sizes?.XXS.value?.iconWidth,
            height: ({ sizes }) => sizes?.XXS.value?.iconHeight,
        },
        [isLeftToRight]: {
            right: ({ sizes }) => sizes?.Default?.value?.paddingHorizontal,
            [BreakPointsMedia.XXL]: {
                right: ({ sizes }) => sizes?.XXL.value?.paddingHorizontal,
            },
            [BreakPointsMedia.XL]: {
                right: ({ sizes }) => sizes?.XL.value?.paddingHorizontal,
            },
            [BreakPointsMedia.LG]: {
                right: ({ sizes }) => sizes?.LG.value?.paddingHorizontal,
            },
            [BreakPointsMedia.MD]: {
                right: ({ sizes }) => sizes?.MD.value?.paddingHorizontal,
            },
            [BreakPointsMedia.SM]: {
                right: ({ sizes }) => sizes?.SM.value?.paddingHorizontal,
            },
            [BreakPointsMedia.XS]: {
                right: ({ sizes }) => sizes?.XS.value?.paddingHorizontal,
            },
            [BreakPointsMedia.XXS]: {
                right: ({ sizes }) => sizes?.XXS.value?.paddingHorizontal,
            },
        },
        [isRightToLeft]: {
            left: ({ sizes }) => sizes?.Default?.value?.paddingHorizontal,
            [BreakPointsMedia.XXL]: {
                left: ({ sizes }) => sizes?.XXL.value?.paddingHorizontal,
            },
            [BreakPointsMedia.XL]: {
                left: ({ sizes }) => sizes?.XL.value?.paddingHorizontal,
            },
            [BreakPointsMedia.LG]: {
                left: ({ sizes }) => sizes?.LG.value?.paddingHorizontal,
            },
            [BreakPointsMedia.MD]: {
                left: ({ sizes }) => sizes?.MD.value?.paddingHorizontal,
            },
            [BreakPointsMedia.SM]: {
                left: ({ sizes }) => sizes?.SM.value?.paddingHorizontal,
            },
            [BreakPointsMedia.XS]: {
                left: ({ sizes }) => sizes?.XS.value?.paddingHorizontal,
            },
            [BreakPointsMedia.XXS]: {
                left: ({ sizes }) => sizes?.XXS.value?.paddingHorizontal,
            },
            transform: 'rotate(180deg)',
        },
        '& > path': {
            fill: 'currentColor',
        },
    },
}, {
    name: 'breadcrumbs',
});
const useBreadCrumbStyles = createUseStyles({
    root: {
        display: 'flex',
        alignItems: 'center',
        height: '100%',
        padding: ({ sizes }) => `${sizes?.Default?.value?.paddingVertical} ${sizes?.Default?.value?.paddingHorizontal}`,
        border: ({ tag }) => tag === BreadCrumbElementTypes.Button && 'none',
        background: ({ tag }) => tag === BreadCrumbElementTypes.Button && 'none',
        fontWeight: ({ sizes }) => sizes?.Default?.value?.fontWeightButtonElements,
        fontSize: ({ sizes }) => sizes?.Default?.value?.fontSize,
        lineHeight: ({ sizes }) => sizes?.Default?.value?.lineHeight,
        transition: `color ${Timings.default} ${Easing.ease}`,
        cursor: ({ tag }) => tag === BreadCrumbElementTypes.Button && 'pointer',
        [BreakPointsMedia.XXL]: {
            padding: ({ sizes }) => `${sizes?.XXL?.value?.paddingVertical} ${sizes?.XXL?.value?.paddingHorizontal}`,
            fontSize: ({ sizes }) => sizes?.XXL?.value?.fontSize,
            lineHeight: ({ sizes }) => sizes?.XXL?.value?.lineHeight,
        },
        [BreakPointsMedia.XL]: {
            padding: ({ sizes }) => `${sizes?.XL?.value?.paddingVertical} ${sizes?.XL?.value?.paddingHorizontal}`,
            fontSize: ({ sizes }) => sizes?.XL?.value?.fontSize,
            lineHeight: ({ sizes }) => sizes?.XL?.value?.lineHeight,
        },
        [BreakPointsMedia.LG]: {
            padding: ({ sizes }) => `${sizes?.LG?.value?.paddingVertical} ${sizes?.LG?.value?.paddingHorizontal}`,
            fontSize: ({ sizes }) => sizes?.LG?.value?.fontSize,
            lineHeight: ({ sizes }) => sizes?.LG?.value?.lineHeight,
        },
        [BreakPointsMedia.MD]: {
            padding: ({ sizes }) => `${sizes?.MD?.value?.paddingVertical} ${sizes?.MD?.value?.paddingHorizontal}`,
            fontSize: ({ sizes }) => sizes?.MD?.value?.fontSize,
            lineHeight: ({ sizes }) => sizes?.MD?.value?.lineHeight,
        },
        [BreakPointsMedia.SM]: {
            padding: ({ sizes }) => `${sizes?.SM?.value?.paddingVertical} ${sizes?.SM?.value?.paddingHorizontal}`,
            fontSize: ({ sizes }) => sizes?.SM?.value?.fontSize,
            lineHeight: ({ sizes }) => sizes?.SM?.value?.lineHeight,
        },
        [BreakPointsMedia.XS]: {
            padding: ({ sizes }) => `${sizes?.XS?.value?.paddingVertical} ${sizes?.XS?.value?.paddingHorizontal}`,
            fontSize: ({ sizes }) => sizes?.XS?.value?.fontSize,
            lineHeight: ({ sizes }) => sizes?.XS?.value?.lineHeight,
        },
        [BreakPointsMedia.XXS]: {
            padding: ({ sizes }) => `${sizes?.XXS?.value?.paddingVertical} ${sizes?.XXS?.value?.paddingHorizontal}`,
            fontSize: ({ sizes }) => sizes?.XXS?.value?.fontSize,
            lineHeight: ({ sizes }) => sizes?.XXS?.value?.lineHeight,
        },
    },
    rootHasIcon: {
        position: 'relative',
        paddingInlineStart: ({ sizes }) => `calc((${sizes?.Default?.value?.paddingHorizontal} * 2) + ${sizes?.Default?.value?.iconWidth}px)`,
        [BreakPointsMedia.XXL]: {
            paddingInlineStart: ({ sizes }) => `calc((${sizes?.XXL?.value?.paddingHorizontal} * 2) + ${sizes?.XXL?.value?.iconWidth}px)`,
        },
        [BreakPointsMedia.XL]: {
            paddingInlineStart: ({ sizes }) => `calc((${sizes?.XL?.value?.paddingHorizontal} * 2) + ${sizes?.XL?.value?.iconWidth}px)`,
        },
        [BreakPointsMedia.LG]: {
            paddingInlineStart: ({ sizes }) => `calc((${sizes?.LG?.value?.paddingHorizontal} * 2) + ${sizes?.LG?.value?.iconWidth}px)`,
        },
        [BreakPointsMedia.MD]: {
            paddingInlineStart: ({ sizes }) => `calc((${sizes?.MD?.value?.paddingHorizontal} * 2) + ${sizes?.MD?.value?.iconWidth}px)`,
        },
        [BreakPointsMedia.SM]: {
            paddingInlineStart: ({ sizes }) => `calc((${sizes?.SM?.value?.paddingHorizontal} * 2) + ${sizes?.SM?.value?.iconWidth}px)`,
        },
        [BreakPointsMedia.XS]: {
            paddingInlineStart: ({ sizes }) => `calc((${sizes?.XS?.value?.paddingHorizontal} * 2) + ${sizes?.XS?.value?.iconWidth}px)`,
        },
        [BreakPointsMedia.XXS]: {
            paddingInlineStart: ({ sizes }) => `calc((${sizes?.XXS?.value?.paddingHorizontal} * 2) + ${sizes?.XXS?.value?.iconWidth}px)`,
        },
    },
    icon: {
        display: 'inline-block',
        width: ({ sizes }) => sizes?.Default?.value?.iconWidth,
        height: ({ sizes }) => sizes?.Default?.value?.iconHeight,
        position: 'absolute',
        top: 0,
        bottom: 0,
        margin: 'auto 0',
        [BreakPointsMedia.XXL]: {
            width: ({ sizes }) => sizes?.XXL?.value?.iconWidth,
            height: ({ sizes }) => sizes?.XXL?.value?.iconHeight,
        },
        [BreakPointsMedia.XL]: {
            width: ({ sizes }) => sizes?.XL?.value?.iconWidth,
            height: ({ sizes }) => sizes?.XL?.value?.iconHeight,
        },
        [BreakPointsMedia.LG]: {
            width: ({ sizes }) => sizes?.LG?.value?.iconWidth,
            height: ({ sizes }) => sizes?.LG?.value?.iconHeight,
        },
        [BreakPointsMedia.MD]: {
            width: ({ sizes }) => sizes?.MD?.value?.iconWidth,
            height: ({ sizes }) => sizes?.MD?.value?.iconHeight,
        },
        [BreakPointsMedia.SM]: {
            width: ({ sizes }) => sizes?.SM?.value?.iconWidth,
            height: ({ sizes }) => sizes?.SM?.value?.iconHeight,
        },
        [BreakPointsMedia.XS]: {
            width: ({ sizes }) => sizes?.XS?.value?.iconWidth,
            height: ({ sizes }) => sizes?.XS?.value?.iconHeight,
        },
        [BreakPointsMedia.XXS]: {
            width: ({ sizes }) => sizes?.XXS?.value?.iconWidth,
            height: ({ sizes }) => sizes?.XXS?.value?.iconHeight,
        },
        [isLeftToRight]: {
            left: ({ sizes }) => sizes?.Default?.value?.paddingHorizontal,
            [BreakPointsMedia.XXL]: {
                left: ({ sizes }) => sizes?.XXL?.value?.paddingHorizontal,
            },
            [BreakPointsMedia.XL]: {
                left: ({ sizes }) => sizes?.XL?.value?.paddingHorizontal,
            },
            [BreakPointsMedia.LG]: {
                left: ({ sizes }) => sizes?.LG?.value?.paddingHorizontal,
            },
            [BreakPointsMedia.MD]: {
                left: ({ sizes }) => sizes?.MD?.value?.paddingHorizontal,
            },
            [BreakPointsMedia.SM]: {
                left: ({ sizes }) => sizes?.SM?.value?.paddingHorizontal,
            },
            [BreakPointsMedia.XS]: {
                left: ({ sizes }) => sizes?.XS?.value?.paddingHorizontal,
            },
            [BreakPointsMedia.XXS]: {
                left: ({ sizes }) => sizes?.XXS?.value?.paddingHorizontal,
            },
        },
        [isRightToLeft]: {
            right: ({ sizes }) => sizes?.Default?.value?.paddingHorizontal,
            [BreakPointsMedia.XXL]: {
                right: ({ sizes }) => sizes?.XXL?.value?.paddingHorizontal,
            },
            [BreakPointsMedia.XL]: {
                right: ({ sizes }) => sizes?.XL?.value?.paddingHorizontal,
            },
            [BreakPointsMedia.LG]: {
                right: ({ sizes }) => sizes?.LG?.value?.paddingHorizontal,
            },
            [BreakPointsMedia.MD]: {
                right: ({ sizes }) => sizes?.MD?.value?.paddingHorizontal,
            },
            [BreakPointsMedia.SM]: {
                right: ({ sizes }) => sizes?.SM?.value?.paddingHorizontal,
            },
            [BreakPointsMedia.XS]: {
                right: ({ sizes }) => sizes?.XS?.value?.paddingHorizontal,
            },
            [BreakPointsMedia.XXS]: {
                right: ({ sizes }) => sizes?.XXS?.value?.paddingHorizontal,
            },
        },
        '& > path': {
            fill: 'currentColor',
        },
    },
}, {
    name: 'breadcrumb',
});
const useBreadCrumbSkinStyles = createUseStyles({
    default: {
        color: ControlTextColors.default,
        [canHover]: {
            '&:hover': {
                color: ControlTextColors.action,
            },
        },
        [canTouch]: {
            '&:active': {
                color: ControlTextColors.action,
            },
        },
    },
    active: {
        color: SecondaryColors.base.default,
        [canHover]: {
            '&:hover': {
                color: ControlTextColors.activeAction,
            },
        },
        [canTouch]: {
            '&:active': {
                color: ControlTextColors.activeAction,
            },
        },
    },
}, {
    name: 'breadcrumb',
});

export { useBreadCrumbSkinStyles, useBreadCrumbStyles, useBreadCrumbsStyles };
